import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  EyeOpenOutlined,
  EyeCloseOutlined,
  ArrowOutlined,
  AssetsOutlined,
  TriangleDownOutlined,
} from '@kufox/icons';
import { css } from '@kufox/mui/emotion';
import { Accordion, Dropdown, Tooltip } from '@kufox/mui';
import useTheme from '@kufox/mui/hooks/useTheme';
import throttle from 'lodash/throttle';
import Link from '../../components/Link';
import { namespace } from '../model';
import CoinCurrency from './CoinCurrency';
import { useLang } from '../../hookTool';
import {
  separateNumber,
  dropZero,
  numberFixed,
  addSpmIntoQuery,
  kcsensorsClick,
} from '../../common/tools';
import buyCryptoIcon from '../../../static/newHeader/buy_crypto.svg';
import depositIcon from '../../../static/newHeader/deposit.svg';
import { long_language } from '../config';

const { AccordionPanel: Panel } = Accordion;
const useStyles = ({ color, inDrawer, isLong_language }) => {
  return {
    nowIcon: css`
      width: 16px;
    `,
    overlayWrapper: css`
      width: ${inDrawer ? 'auto' : isLong_language ? '400px' : '300px'};
      margin-top: 22px;
      margin-bottom: ${inDrawer ? '16px' : '0'};
      padding: ${inDrawer ? '0' : '16px 0'};
      background: ${inDrawer ? 'transparent' : color.base};
      box-shadow: ${inDrawer ? 'unset' : '0px 10px 60px rgba(0, 0, 0, 0.1)'};
      border-radius: 4px;
      & hr {
        margin: 8px 28px;
      }
    `,
    menuTitleItem: css`
      display: block;
      padding: 14px 32px;
      cursor: pointer;
      color: ${color.text};
      text-decoration: none !important;
      font-size: ${inDrawer ? '14px' : '16px'};
      line-height: 1.6;
      position: relative;
      &:hover {
        background: ${color.cover4};
      }
      @keyframes move {
        0% {
          right: 20px;
        }
        100% {
          right: 18px;
        }
      }
      @keyframes changeColor {
        0% {
          fill: #737e8d;
        }
        100% {
          fill: #18bb97;
        }
      }
      & .arrow {
        display: none;
      }
      &:hover .arrow {
        display: block;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: -10px;
        animation: move 0.3s linear forwards;
        -webkit-animation: move 0.3s linear forwards;
        & path {
          animation: changeColor 0.3s linear forwards;
          -webkit-animation: changeColor 0.3s linear forwards;
        }
      }
    `,
    menuItem: css`
      display: flex;
      width: 100%;
      height: ${inDrawer ? '48px' : '48px'};
      padding: ${inDrawer ? '0 24px' : `0 32px`};
      align-items: center;
      cursor: pointer;
      color: ${color.text};
      text-decoration: none !important;
      font-size: ${inDrawer ? '14px' : '16px'};
      line-height: 1.6;
      position: relative;
      &:hover {
        background: ${color.cover4};
        color: ${color.text};
      }
      @keyframes move {
        0% {
          right: 20px;
        }
        100% {
          right: 18px;
        }
      }
      @keyframes changeColor {
        0% {
          fill: #737e8d;
        }
        100% {
          fill: #18bb97;
        }
      }
      & .arrow {
        display: none;
      }
      &:hover .arrow {
        display: block;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: -10px;
        animation: move 0.3s linear forwards;
        -webkit-animation: move 0.3s linear forwards;
        & path {
          animation: changeColor 0.3s linear forwards;
          -webkit-animation: changeColor 0.3s linear forwards;
        }
      }
      & .textIcon {
        display: inline-block;
        width: 24px;
        height: 24px;
        line-height: 24px;
        text-align: center;
        border-radius: 6px;
        font-size: 12px;
        color: ${color.base};
        font-weight: bold;
        margin-right: 16px;
      }
      & .ma {
        background: ${color.primary};
      }
      & .tr {
        background: ${color.trade};
      }
      & .mg {
        background: ${color.margin};
      }
      & .fu {
        background: ${color.futures};
      }
      & .px {
        background: ${color.poolx};
      }
    `,
    menuItemText: css`
      box-sizing: border-box;
      flex: 1;
      word-break: break-all;
      white-space: break-spaces;
      line-height: 20px;
    `,
    menuTitleItemText: css`
      white-space: break-spaces;
      line-height: 20px;
    `,
    assetsDetail: css`
      display: flex;
      align-items: center;
      color: ${color.text60};
      font-size: 14px;
      line-height: 1.5;
      word-break: break-all;
      flex-wrap: wrap;
      & svg {
        width: 20px;
        height: 20px;
        color: #fff;
        margin-left: 6px;
        margin-right: 8px;
        flex-shrink: 0;
      }
    `,
    assetsCount: css`
      color: ${color.text60};
      font-size: 14px;
      line-height: 1.6;
      & .account {
        color: ${color.text};
        font-size: 20px;
        line-height: 1.6;
        margin-right: 8px;
      }
    `,
    legalCurrency: css`
      color: ${color.text60};
      font-size: 14px;
      line-height: 1.4;
    `,
    hr: css`
      background: ${color.divider};
      border: none;
      height: 1px;
    `,
    bottomMenu: css`
      padding: 0;
      padding-bottom: ${inDrawer ? `0` : '8px'};
      display: flex;
      align-items: center;
      justify-content: space-between;
      & span {
        flex: 1;
      }
      & .item {
        width: 100%;
        height: ${inDrawer ? `48px` : '60px'};
        background: ${color.base};
        padding-left: ${inDrawer ? `8px` : '16px'};
        padding-right: ${inDrawer ? `8px` : '12px'};
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: ${inDrawer ? `14px` : '16px'};
        line-height: 1.6;
        color: ${color.text};
        text-decoration: none !important;
        & img {
          width: 44px;
          margin-right: 10px;
        }

        &:hover {
          background: ${color.cover4};
        }
      }
    `,
    tooltipWrapper: css`
      z-index: 12000;
      & > div {
        background: #4a5368;
        border-radius: 4px;
        color: #fff;
      }
      & > div > div {
        color: #fff;
      }
      & > div > span {
        border-color: #4a5368 transparent transparent transparent !important;
      }
    `,
    accordion: css`
      .KuxAccordion-head {
        padding: 8px 24px;
        font-size: 14px;
      }
    `,
  };
};

export default (props) => {
  const {
    currency,
    hostConfig,
    isSub = false,
    userInfo,
    currentLang,
    className = '',
    themeColors,
    title,
    inDrawer,
    onClose,
  } = props;
  const theme = useTheme();
  const color = themeColors || theme.colors;
  const isLong_language = long_language.indexOf(currentLang) > -1;
  const classes = useStyles({ color, inDrawer, isLong_language });

  const [showAssets, setShowAssets] = useState(localStorage.getItem('SHOW_ASSETS') || '');

  const { t } = useLang();

  const { assetDetail } = useSelector((state) => state[namespace]);
  const dispatch = useDispatch();

  const changeAssetShow = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const visible = !showAssets;
    setShowAssets(visible);
    localStorage.getItem('SHOW_ASSETS', visible);
  };
  const handleMouseEnter = throttle(
    () => {
      const { balanceCurrency = 'USDT' } = userInfo || {};
      dispatch({
        type: `${namespace}/pullAssetDetail`,
        payload: {
          balanceCurrency,
        },
      });
    },
    2000,
    { leading: true },
  );

  const handleRouter = useCallback(() => {
    kcsensorsClick(['assets', '1']);
    const { KUCOIN_HOST } = hostConfig;
    window.location.href = addSpmIntoQuery(
      `${KUCOIN_HOST}/assets?lang=${currentLang}`,
      ['assets', '1'],
      currentLang,
    );
  }, [currentLang]);

  const Overlay = () => {
    const { KUCOIN_HOST, KUMEX_HOST } = hostConfig;
    const { totalAssets: _total, balanceCurrency } = assetDetail || {};
    const totalAssets = _total || 0;
    const { showSubAccountBalanceSwitch } = assetDetail || {};
    return (
      <div css={classes.overlayWrapper}>
        {!inDrawer && (
          <>
            <Link
              href={addSpmIntoQuery(`${KUCOIN_HOST}/assets`, ['assets', '2'], currentLang)}
              css={classes.menuTitleItem}
              data-ga="assets"
              data-modid="assets"
              data-idx={2}
              lang={currentLang}
            >
              <div css={classes.assetsDetail}>
                <span>{t('asset.overview')}</span>
                {showAssets ? (
                  <EyeOpenOutlined onClick={changeAssetShow} size={24} color={color.text} />
                ) : (
                  <EyeCloseOutlined onClick={changeAssetShow} size={24} color={color.text} />
                )}
                <span css={classes.menuTitleItemText}>
                  {showSubAccountBalanceSwitch && t('include.sub.assets')}
                </span>
              </div>
              <div css={classes.assetsCount}>
                <span className="account">
                  {showAssets ? separateNumber(dropZero(numberFixed(totalAssets, 8))) : '***'}
                </span>{' '}
                <span style={{ color: color.text60 }}>{balanceCurrency}</span>
              </div>
              <div css={classes.legalCurrency}>
                {showAssets ? (
                  <CoinCurrency hideLegalCurrency value={totalAssets} coin={balanceCurrency} />
                ) : (
                  '***'
                )}{' '}
                {currency}
              </div>
              {!inDrawer && <ArrowOutlined width="13px" height="13px" className="arrow" />}
            </Link>
            <hr css={classes.hr} />
          </>
        )}
        <Link
          href={addSpmIntoQuery(`${KUCOIN_HOST}/assets/main-account`, ['assets', '3'])}
          css={classes.menuItem}
          data-modid="assets"
          data-idx={3}
          lang={currentLang}
          onClick={onClose || null}
        >
          <span className="textIcon ma">Ma</span>
          {/* {isSub ? t('main.account') : t('drop.save.account')} */}
          <span css={classes.menuItemText}>{t('main.account')}</span>
          {!inDrawer && <ArrowOutlined width="13px" height="13px" className="arrow" />}
        </Link>
        <Link
          href={addSpmIntoQuery(
            `${KUCOIN_HOST}/assets/trade-account`,
            ['assets', '4'],
            currentLang,
          )}
          css={classes.menuItem}
          data-modid="assets"
          data-idx={4}
          lang={currentLang}
          onClick={onClose || null}
        >
          <span className="textIcon tr">Tr</span>
          <span css={classes.menuItemText}>{t('trade.account')}</span>
          {!inDrawer && <ArrowOutlined width="13px" height="13px" className="arrow" />}
        </Link>
        <Link
          href={addSpmIntoQuery(
            `${KUCOIN_HOST}/assets/margin-account`,
            ['assets', '5'],
            currentLang,
          )}
          css={classes.menuItem}
          data-modid="assets"
          data-idx={5}
          lang={currentLang}
          onClick={onClose || null}
        >
          <span className="textIcon mg">Mg</span>
          <span css={classes.menuItemText}>{t('margin.margin.account')}</span>
          {!inDrawer && <ArrowOutlined width="13px" height="13px" className="arrow" />}
        </Link>
        <Link
          href={addSpmIntoQuery(`${KUMEX_HOST}/assets`, ['assets', '6'], currentLang)}
          css={classes.menuItem}
          data-modid="assets"
          data-idx={6}
          lang={currentLang}
          onClick={onClose || null}
        >
          <span className="textIcon fu">Fu</span>
          <span css={classes.menuItemText}>{t('margin.account')}</span>
          {!inDrawer && <ArrowOutlined width="13px" height="13px" className="arrow" />}
        </Link>
        <Link
          href={addSpmIntoQuery(`${KUCOIN_HOST}/assets/earn-account`, ['assets', '7'], currentLang)}
          css={classes.menuItem}
          data-modid="assets"
          data-idx={7}
          lang={currentLang}
          onClick={onClose || null}
        >
          <span className="textIcon px">Er</span>
          <span className={classes.menuItemText}>{t('pool.earnAccount.account')}</span>
          {!inDrawer && <ArrowOutlined width="13px" height="13px" className="arrow" />}
        </Link>
        <hr css={classes.hr} />
        {!isSub && (
          <div css={classes.bottomMenu}>
            <Tooltip
              title={t('newheader.buy.crypto.tips')}
              css={classes.tooltipWrapper}
              placement="top"
              enterDelay={3000}
            >
              <a
                href={addSpmIntoQuery(`${KUCOIN_HOST}/express`, ['assets', '8'])}
                className="item"
                data-ga="assets"
                data-modid="assets"
                data-idx={8}
                lang={currentLang}
              >
                <img src={buyCryptoIcon} alt="buy" />
                {t('newheader.buy.crypto')}
              </a>
            </Tooltip>
            <Tooltip
              title={t('newheader.deposit.tips')}
              css={classes.tooltipWrapper}
              placement="top"
              enterDelay={3000}
            >
              <a
                href={addSpmIntoQuery(`${KUCOIN_HOST}/assets/coin`, ['assets', '9'])}
                className="item itemLeft"
                data-ga="assets"
                data-modid="assets"
                data-idx={9}
                lang={currentLang}
              >
                <img src={depositIcon} alt="deposit" />
                {t('newheader.deposit')}
              </a>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };
  if (inDrawer) {
    return (
      <Accordion css={classes.accordion}>
        <Panel header={title} key={0}>
          <Overlay />
        </Panel>
      </Accordion>
    );
  }
  return (
    <Dropdown
      overlay={<Overlay />}
      trigger="hover"
      anchorProps={{ style: { 'display': 'block' } }}
      placement="bottom"
    >
      <div onClick={handleRouter} className={className || ''} onMouseEnter={handleMouseEnter}>
        <AssetsOutlined size="36" className="navIcon" />
      </div>
    </Dropdown>
  );
};
